import React, { useState } from 'react';
import { usePlaybook } from '../../context/PlaybookContext';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const ScenarioManager = ({ projectId }) => {
  const { scenarios, createIndustryScenarioPack, partnerPlaybooks, applyPartnerPlaybook } = usePlaybook();
  const { currentUser } = useCRM();
  
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('Alle');
  const [showPackSelector, setShowPackSelector] = useState(false);

  const projectScenarios = scenarios.filter(s => s.project_id === projectId);
  
  // Filter logic
  const categories = ['Alle', ...new Set(projectScenarios.map(s => s.category))];
  const filteredScenarios = filter === 'Alle' ? projectScenarios : projectScenarios.filter(s => s.category === filter);

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'Første kontakt': return 'bg-blue-100 text-blue-800';
      case 'Discovery': return 'bg-indigo-100 text-indigo-800';
      case 'Demo': return 'bg-purple-100 text-purple-800';
      case 'Pris/forhandling': return 'bg-amber-100 text-amber-800';
      case 'Closing': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const industries = [
    { id: 'Standard', label: 'Standard Pack', icon: '📦' },
    { id: 'SaaS', label: 'SaaS / Tech', icon: '💻' },
    { id: 'HR', label: 'HR & Rekruttering', icon: '👥' },
    { id: 'HMS', label: 'HMS & Compliance', icon: '🦺' },
    { id: 'Konsulent', label: 'Konsulent / Byrå', icon: '👔' }
  ];

  const handleCreatePack = (industry) => {
    createIndustryScenarioPack(projectId, industry);
    setShowPackSelector(false);
  };

  const handleApplyPartnerPack = (ppbId) => {
    applyPartnerPlaybook(projectId, ppbId);
    setShowPackSelector(false);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-xl font-black text-slate-900">Salgsscenarioer</h3>
          <p className="text-sm text-slate-500">Playbook for kritiske øyeblikk i salgsprosessen.</p>
        </div>
        
        {/* Pack Selector Button */}
        {projectScenarios.length === 0 && currentUser.role !== 'Customer_Viewer' && (
          <div className="relative">
            {!showPackSelector ? (
              <button 
                onClick={() => setShowPackSelector(true)}
                className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg"
              >
                <SafeIcon icon="⚡" /> Opprett Playbook Pack
              </button>
            ) : (
              <div className="bg-white p-2 rounded-xl shadow-xl border border-slate-200 absolute top-0 right-0 z-10 w-72 animate-in fade-in zoom-in duration-200">
                <div className="flex justify-between items-center px-3 py-2 border-b border-slate-100 mb-2">
                  <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Velg Mal</span>
                  <button onClick={() => setShowPackSelector(false)}><SafeIcon icon="✕" className="w-3 h-3 text-slate-400" /></button>
                </div>
                
                {/* Standard Packs */}
                <div className="space-y-1 mb-3">
                  <p className="px-3 text-[10px] font-bold text-slate-300 uppercase">Standard Pakker</p>
                  {industries.map(ind => (
                    <button key={ind.id} onClick={() => handleCreatePack(ind.id)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-slate-50 rounded-lg text-left transition-colors">
                      <span className="text-lg">{ind.icon}</span>
                      <span className="text-sm font-bold text-slate-700">{ind.label}</span>
                    </button>
                  ))}
                </div>

                {/* Partner Packs */}
                {partnerPlaybooks.length > 0 && (
                  <div className="space-y-1 border-t border-slate-100 pt-2">
                    <p className="px-3 text-[10px] font-bold text-slate-300 uppercase">Partner Playbooks</p>
                    {partnerPlaybooks.map(ppb => (
                      <button key={ppb.ppb_id} onClick={() => handleApplyPartnerPack(ppb.ppb_id)} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-purple-50 rounded-lg text-left transition-colors group">
                        <span className="text-lg">⭐</span>
                        <div>
                          <span className="block text-sm font-bold text-purple-900 group-hover:text-purple-700">{ppb.name}</span>
                          <span className="block text-[10px] text-purple-400">{ppb.industry}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Categories Filter */}
      {projectScenarios.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
          {categories.map(cat => (
            <button 
              key={cat} 
              onClick={() => setFilter(cat)}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-all border ${filter === cat ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Scenario List */}
      <div className="space-y-4">
        {filteredScenarios.length > 0 ? filteredScenarios.map((scenario) => (
          <motion.div layout key={scenario.id} className={`bg-white rounded-2xl border transition-all overflow-hidden ${expandedId === scenario.id ? 'border-primary-300 shadow-md ring-1 ring-primary-100' : 'border-slate-200 hover:border-slate-300'}`}>
            
            {/* Row Header */}
            <div onClick={() => setExpandedId(expandedId === scenario.id ? null : scenario.id)} className="p-5 cursor-pointer flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-lg ${expandedId === scenario.id ? 'bg-primary-600 text-white' : 'bg-slate-50 text-slate-400'}`}>
                  <SafeIcon icon={expandedId === scenario.id ? "📖" : "📘"} />
                </div>
                <div>
                  <h4 className="font-bold text-slate-900">{scenario.name}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${getCategoryColor(scenario.category)}`}>
                      {scenario.category}
                    </span>
                    {scenario.source && (
                      <span className="text-[10px] text-purple-500 font-bold flex items-center gap-1">
                        <SafeIcon icon="⭐" className="w-2 h-2" /> {scenario.source}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <SafeIcon icon="▼" className={`text-slate-300 transition-transform ${expandedId === scenario.id ? 'rotate-180' : ''}`} />
            </div>

            {/* Expanded Content */}
            <AnimatePresence>
              {expandedId === scenario.id && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-slate-100 bg-slate-50/50">
                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <h5 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                          <SafeIcon icon="✅" className="text-emerald-500" /> Sjekkliste
                        </h5>
                        <ul className="space-y-2">
                          {scenario.checklist ? scenario.checklist.split('\n').map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-700 font-medium">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                              {item.replace(/^- /, '')}
                            </li>
                          )) : <li className="text-sm text-slate-400">Ingen sjekkliste.</li>}
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div className="bg-white p-5 rounded-xl border border-slate-200">
                        <h5 className="text-xs font-black text-slate-900 uppercase tracking-widest mb-2">Målsetting</h5>
                        <p className="text-sm text-slate-600">{scenario.goal}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

          </motion.div>
        )) : (
          <div className="text-center py-20 border-2 border-dashed border-slate-200 rounded-[2rem]">
            <SafeIcon icon="🎭" className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900">Ingen scenarioer</h3>
            <p className="text-slate-500 mb-6">Velg en pakke ovenfor for å komme i gang.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScenarioManager;